import React, {FC} from 'react';

import {Trans} from '../../../../../libs/shared/core/src/helpers/internationalization/react';
import {ChannelTypesEnum} from '../../../../../libs/shared/core/src/models/api/channelCommonTypes';
import {DropdownItem} from '../../../../../libs/shared/ui/src/components/dropdowns/items/dropdownItem';
import {DropdownItemContent} from '../../../../../libs/shared/ui/src/components/dropdowns/items/dropdownItemContent';
import {DropdownItemIcon} from '../../../../../libs/shared/ui/src/components/dropdowns/items/dropdownItemIcon';
import {SimpleDropdown} from '../../../../../libs/shared/ui/src/components/dropdowns/simpleDropdown/simpleDropdown';
import {ChannelIcon} from '../../../../../libs/shared/ui/src/components/icon/channelIcon';
import {PopoverTransferProps} from '../../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';

/*
 * Constants.
 */

const dropdownWidth = 180; // px

/*
 * Props.
 */

interface ChannelTypesDropdownProps {
  channelTypes: ReadonlyArray<ChannelTypesEnum>;
  popoverProps: PopoverTransferProps;
  onSourceSelect: (arg: ChannelTypesEnum) => void;
}

/*
 * Component.
 */

export const ChannelTypesDropdown: FC<ChannelTypesDropdownProps> = (props) => {
  const {channelTypes, popoverProps, onSourceSelect} = props;

  return (
    <SimpleDropdown {...popoverProps} width={dropdownWidth}>
      {channelTypes.map((channelType) => {
        const channelName = renderChannelTypeName(channelType);
        return (
          <DropdownItem key={channelType} onSelect={() => onSourceSelect(channelType)}>
            <DropdownItemIcon label={channelName}>
              <ChannelIcon type={channelType} />
            </DropdownItemIcon>
            <DropdownItemContent>{channelName}</DropdownItemContent>
          </DropdownItem>
        );
      })}
    </SimpleDropdown>
  );
};

/*
 * Helpers.
 */

function renderChannelTypeName(channelType: ChannelTypesEnum) {
  switch (channelType) {
    case ChannelTypesEnum.CUSTOM:
      return <Trans id="channel-type-dropdown-item-custom">Custom channel</Trans>;
    case ChannelTypesEnum.FRONT_MAIL:
    case ChannelTypesEnum.IMAP:
    case ChannelTypesEnum.SMTP:
    case ChannelTypesEnum.OFFICE365:
    case ChannelTypesEnum.GMAIL:
      return <Trans id="channel-type-dropdown-item-email">Email</Trans>;
    case ChannelTypesEnum.GOOGLE_PLAY:
      return <Trans id="channel-type-dropdown-item-google-play">Google Play</Trans>;
    case ChannelTypesEnum.LAYER_ANON:
    case ChannelTypesEnum.FRONT_CHAT:
      return <Trans id="channel-type-dropdown-item-front-chat">Front chat</Trans>;
    case ChannelTypesEnum.PORTAL:
      return <Trans id="channel-type-dropdown-item-portal">Customer portal</Trans>;
    case ChannelTypesEnum.TWILIO:
      return <Trans id="channel-type-dropdown-item-sms">SMS</Trans>;
    case ChannelTypesEnum.TWILIO_WHATSAPP:
      return <Trans id="channel-type-dropdown-item-twilio-whatsapp">Twilio/Whatsapp</Trans>;
    case ChannelTypesEnum.YALO_WHATSAPP:
      return <Trans id="channel-type-dropdown-item-yalo-whatsapp">Yalo/Whatsapp</Trans>;
    case ChannelTypesEnum.INTERCOM:
      return <Trans id="channel-type-dropdown-item-intercom">Intercom</Trans>;
    case ChannelTypesEnum.SMOOCH:
      return <Trans id="channel-type-dropdown-item-smooch">Smooch</Trans>;
    case ChannelTypesEnum.TALKDESK:
      return <Trans id="channel-type-dropdown-item-talkdesk">Talkdesk</Trans>;
    case ChannelTypesEnum.TRULY:
      return <Trans id="channel-type-dropdown-item-truly">Truly</Trans>;
    case ChannelTypesEnum.TWITTER:
      return <Trans id="channel-type-dropdown-item-twitter">X</Trans>;
    case ChannelTypesEnum.TWITTER_DM:
      return <Trans id="channel-type-dropdown-item-twitter-dm">X DM</Trans>;
    case ChannelTypesEnum.FACEBOOK:
      return <Trans id="channel-type-dropdown-item-facebook">Messenger</Trans>;
    default:
      return null;
  }
}
